import { ApiClient } from "./ApiClient";
import { RealmLower } from "../constants";
import { ServiceResult } from "./index";
import { toErrorMessage } from "../utils/ErrorMessage";

export type FailureStatus = "FAILED" | "TIMED_OUT" | "ABORTED";

export interface StateMachineSummary {
  name: string;
  arn: string;
}

export interface FailedExecution {
  name: string;
  executionArn: string;
  status: FailureStatus;
  startDate: string;
  stopDate?: string;
  workflowError?: { error?: string; cause?: string };
}

export interface FailedExecutionsResult {
  executions: FailedExecution[];
  truncated: boolean;
}

export interface FailedExecutionsQuery {
  stateMachineArn: string;
  startDate: Date;
  endDate: Date;
  statuses: FailureStatus[];
}

export class StepFunctionFailuresService {
  private api: ApiClient;

  constructor(api: ApiClient = ApiClient.getInstance()) {
    this.api = api;
  }

  async listStateMachines(realm: RealmLower): Promise<ServiceResult<StateMachineSummary[]>> {
    try {
      const base = this.api.getBaseUrl("workflow-statuses", realm);
      const region = this.api.getRegionCode(realm);
      const data = await this.api.get<StateMachineSummary[]>(`${base}/state-machines?region=${region}`);
      return { status: "ok", data };
    } catch (err) {
      return { status: "error", message: toErrorMessage(err) };
    }
  }

  async getFailedExecutions(realm: RealmLower, query: FailedExecutionsQuery): Promise<ServiceResult<FailedExecutionsResult>> {
    try {
      const base = this.api.getBaseUrl("workflow-statuses", realm);
      const params = new URLSearchParams({
        region: this.api.getRegionCode(realm),
        stateMachineArn: query.stateMachineArn,
        startDate: query.startDate.toISOString(),
        endDate: query.endDate.toISOString(),
        statuses: query.statuses.join(","),
      });
      const data = await this.api.get<FailedExecutionsResult>(`${base}/step-function-failures?${params}`);
      return { status: "ok", data };
    } catch (err) {
      return { status: "error", message: toErrorMessage(err) };
    }
  }
}
